// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, mergeMap, tap } from 'rxjs/operators';

import { SelectablesResponseVehicleComponent, VehicleComponent } from 'src/app/api/owcc/models';
import { ActivatedRouteState } from 'src/app/core/services/activated-route/activated-route-state.interface';
import { TrackingAction } from 'src/app/core/services/tracking/models/tracking-action.enum';
import { TrackingCategory } from 'src/app/core/services/tracking/models/tracking-category.enum';
import { TrackingEventType } from 'src/app/core/services/tracking/models/tracking-event-type.enum';
import { TrackingLabel } from 'src/app/core/services/tracking/models/tracking-label.enum';
import { TrackingInput, TrackingSearch } from 'src/app/core/services/tracking/models/tracking-object.interface';
import { TrackingPagename } from 'src/app/core/services/tracking/models/tracking-pagename.enum';
import { TrackingDataService } from 'src/app/core/services/tracking/tracking-data.service';
import { TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { FeasibilityResult } from 'src/app/shared/feasibility-conflict-modal/feasibility-result.interface';
import {
  selectConfigurationSelectableChangeSource,
  selectConfigurationSelectableComponentById,
  selectConfigurationSelectableComponentsOrUndefined,
  selectConfigurationVehicleBrand,
  selectFilterResults
} from 'src/app/store/common/common.selectors';
import { EffectsBase } from 'src/app/store/effects-base';
import { RouterActionType } from 'src/app/store/router/router.actions';
import { selectFilteredPreviousRouteState, selectRouteState } from 'src/app/store/router/router.selectors';
import { filterChanged, filterTruthy } from 'src/app/utils/functions/rx-filters';
import { VehicleComponentCollection } from 'src/app/utils/models/vehicle-component-collection.interface';

import {
  abortFeasibilityConflictAction,
  finishResolveFeasibilityConflictAction,
  onlineCodeSectionInViewportAction,
  startResolveFeasibilityConflictAction,
  startUpdateFeasibleVehicleAction
} from './configuration.actions';

@Injectable()
export class ConfigurationTrackingEffects extends EffectsBase {
  // DO NOT REMOVE, even though it might appear as unused in your IDE
  public trackSelectableChangeWithoutConflict$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(startUpdateFeasibleVehicleAction),
        mergeMap(({ conflictItem }) =>
          combineLatest([
            this.store.select(selectRouteState).pipe(filterTruthy(), first()),
            of(conflictItem),
            this.store.select(selectConfigurationSelectableComponentsOrUndefined).pipe(filterTruthy(), first()),
            this.store.select(selectConfigurationSelectableChangeSource).pipe(first()),
            this.store.select(selectFilterResults).pipe(first()),
            this.store.select(selectConfigurationSelectableComponentById(conflictItem.id)).pipe(filterTruthy(), first())
          ])
        ),
        map(([activatedRoute, conflictItem, , source, search, component]) => {
          const trackingInput: TrackingInput = {
            event: TrackingEventType.Click,
            routeState: activatedRoute.isSummary ? TrackingPagename.Summary : activatedRoute.configurationStep,
            clickData: {
              category: search ? TrackingCategory.Search : TrackingCategory.Feature,
              label: TrackingDataService.getLabelItemChange(TrackingLabel.ItemChange, conflictItem, component?.labels),
              action: source,
              search
            },
            conflictItem
          };
          this.trackingService.sendTrackingData(trackingInput);
        })
      ),
    { dispatch: false }
  );

  // DO NOT REMOVE, even though it might appear as unused in your IDE
  public trackFeasabilityConflict$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(startResolveFeasibilityConflictAction),
        mergeMap(({ alternatives, conflictItem }) =>
          combineLatest([
            this.store.select(selectRouteState).pipe(filterTruthy(), first()),
            of(alternatives),
            of(conflictItem),
            this.store.select(selectConfigurationSelectableComponentsOrUndefined).pipe(filterTruthy(), first()),
            this.store.select(selectFilterResults).pipe(first()),
            this.store.select(selectConfigurationSelectableComponentById(conflictItem.id)).pipe(filterTruthy(), first())
          ])
        ),
        tap(([activatedRoute, alternatives, conflictItem, components, search, vehicleComponent]) => {
          const trackingInput: TrackingInput = TrackingDataService.getTrackingInputForConflicts(
            TrackingLabel.FeasibilityLoad,
            activatedRoute,
            { alternatives, conflictItem } as FeasibilityResult,
            components,
            search,
            vehicleComponent?.labels
          );
          this.trackingService.sendTrackingData(trackingInput);
        })
      ),
    { dispatch: false }
  );

  // DO NOT REMOVE, even though it might appear as unused in your IDE
  public trackResolvedFeasibilityConflict$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(finishResolveFeasibilityConflictAction),
        mergeMap(payload => this.mapFeasibilityResult(payload)),
        map(([activatedRoute, feasibilityResult, components, search, component]) => {
          const trackingInput = TrackingDataService.getTrackingInputForConflicts(
            TrackingLabel.ItemChange,
            activatedRoute,
            feasibilityResult,
            components,
            search,
            component?.labels
          );
          this.trackingService.sendTrackingData(trackingInput);
        })
      ),
    { dispatch: false }
  );

  // DO NOT REMOVE, even though it might appear as unused in your IDE
  public trackAbortedFeasibilityConflict$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(abortFeasibilityConflictAction),
        mergeMap(payload => this.mapFeasibilityResult(payload)),
        map(([activatedRoute, feasibilityResult, components, search, component]) => {
          const trackingInput: TrackingInput = TrackingDataService.getTrackingInputForConflicts(
            TrackingLabel.FeasibilityAbort,
            activatedRoute,
            feasibilityResult,
            components,
            search,
            component?.labels
          );
          this.trackingService.sendTrackingData(trackingInput);
        })
      ),
    { dispatch: false }
  );

  public trackingPageChange$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(RouterActionType.RouterNavigationEnd),
        mergeMap(() =>
          combineLatest([
            this.store.select(selectRouteState).pipe(filterTruthy(), filterChanged(), first()),
            this.store.pipe(selectFilteredPreviousRouteState, first()),
            this.store.select(selectConfigurationVehicleBrand).pipe(first())
          ])
        ),
        map(([activatedRoute, previousRoute, brand]) => {
          if (
            (activatedRoute.isConfiguration &&
              activatedRoute.configurationStep &&
              (!previousRoute?.appRoutePath ||
                activatedRoute?.configurationStep !== previousRoute?.configurationStep)) ||
            activatedRoute.isVehicleLength
          ) {
            let routeState: TrackingPagename | string = activatedRoute.configurationStep;
            if (activatedRoute.isSummary) {
              routeState = TrackingPagename.Summary;
            }
            const trackingInput: TrackingInput = {
              event: TrackingEventType.PageView,
              routeState: routeState,
              brand: brand
            };

            this.trackingService.sendTrackingData(trackingInput);
          }
        })
      ),
    { dispatch: false }
  );

  // DO NOT REMOVE, even though it might appear as unused in your IDE
  public trackOnlineCodeSectionInViewport$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(onlineCodeSectionInViewportAction),
        map(() => {
          const trackingInput: TrackingInput = {
            event: TrackingEventType.Click,
            clickData: {
              label: '',
              category: TrackingCategory.Impression,
              action: TrackingAction.OnlineCode
            }
          };

          this.trackingService.sendTrackingData(trackingInput);
        })
      ),
    { dispatch: false }
  );

  public constructor(
    private readonly trackingService: TrackingService,
    private readonly store: Store
  ) {
    super();
  }

  private readonly mapFeasibilityResult = ({
    feasibilityResult
  }: {
    feasibilityResult?: FeasibilityResult;
  }): Observable<
    [
      ActivatedRouteState,
      FeasibilityResult,
      VehicleComponentCollection,
      TrackingSearch,
      VehicleComponent | SelectablesResponseVehicleComponent
    ]
  > =>
    combineLatest([
      this.store.select(selectRouteState).pipe(filterTruthy(), first()),
      of(feasibilityResult),
      this.store.select(selectConfigurationSelectableComponentsOrUndefined).pipe(filterTruthy(), first()),
      this.store.select(selectFilterResults).pipe(first()),
      this.store
        .select(selectConfigurationSelectableComponentById(feasibilityResult?.conflictItem?.id))
        .pipe(filterTruthy(), first())
    ]);
}
